import { useCallback, useContext, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router";

import {
  Button,
  Buttons,
  Icon,
  Link,
  LoadingGroup,
  LoadingSkeleton,
  Message,
  Messages,
  Tag,
  TagType,
} from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Body, Bold, Heading, Semibold, Subtitle } from "capitalrx-components-2.0/lib/typography";
import { CaseTags } from "capitalrx-types";
import { formatDate } from "capitalrx-utilities";
import styled from "styled-components";

import {
  PriorAuthorizationDecisionSubLetter,
  getPriorAuthorizationDocumentsUrl,
  useGetPriorAuthorizationCaseQuery,
  useListPriorAuthorizationDecisionLetter,
} from "@/App/api/priorAuthorization";
import { ContentPanel, NoResults, PageWrapper, ThemedButton } from "@/App/components";
import { StackOrShelf } from "@/App/components/StackOrShelf";
import { SearchParamKeys } from "@/App/constants/constants";
import { AuthenticationContext, ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { useNavigateClientNewTab } from "@/App/hooks";
import { RenewalRequestFormModal } from "@/App/pages/PriorAuthorization/RenewalRequestFormModal";
import { enablePriorAuthorizationRenewalRequest } from "@/App/utils/featureFlags";
import { formatPhoneNumber, getCustomerSupportNumber } from "@/App/utils/utils";

const TRANSLATION_PATH = "PRIOR_AUTHORIZATION.priorAuthorizationDetailsPage";

export const PriorAuthorizationDetailsPage = () => {
  const { t: translate } = useTranslation();
  const navigateNewTab = useNavigateClientNewTab();
  const location = useLocation();
  const { memberEligibility, isViewingDataholder } = useContext(MemberEligibilityContext);
  const { priorAuthorizationPhoneNumber } = useContext(ExperienceContext);
  const customerSupportNumber = priorAuthorizationPhoneNumber ? priorAuthorizationPhoneNumber : getCustomerSupportNumber(memberEligibility);
  const { priorAuthorizationId } = useParams();
  const [renewalFormIsVisible, setRenewalFormIsVisible] = useState(false);
  const memberCameFromHomePage = location.state?.previousPage === "home";

  const {
    data: priorAuthorizationCaseData,
    isLoading: paCaseIsLoading,
    isLoadingError: isLoadingPACaseError,
    isRefetching: paCaseIsRefetching,
    isError: isPAFetchingError,
    refetch: refetchPaCase,
  } = useGetPriorAuthorizationCaseQuery({ id: +priorAuthorizationId });
  const { getAuthToken } = useContext(AuthenticationContext);

  const {
    data: documentListData,
    isLoading: isLoadingDocument,
    isError: isErrorDocument,
  } = useListPriorAuthorizationDecisionLetter({ case_id: +priorAuthorizationId });

  const caseOutcomesObject = {
    approved: [translate(`${TRANSLATION_PATH}.approved`), "success-high-contrast"],
    denied: [translate(`${TRANSLATION_PATH}.denied`), "error-high-contrast"],
    closed: [translate(`${TRANSLATION_PATH}.closed`), "info-high-contrast"],
    in_progress: [translate(`${TRANSLATION_PATH}.inProgress`), "warning-high-contrast"],
  };
  const caseOutcome = priorAuthorizationCaseData?.case_outcome?.outcome || "in_progress";

  const dateFormat = "YYYY-MM-DD";

  const formattedPrescriberPhoneNumber = useMemo(() => {
    return formatPhoneNumber(priorAuthorizationCaseData?.prescriber_business_practice_phone);
  }, [priorAuthorizationCaseData?.prescriber_business_practice_phone]);

  const renderOutcomeSection = () => {
    if (priorAuthorizationCaseData?.case_outcome?.outcome === "approved") {
      return (
        <>
          <Body>
            <Semibold>{translate(`${TRANSLATION_PATH}.approved`)}: </Semibold>
            {formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}
          </Body>
          <Body>
            <Semibold>{translate(`${TRANSLATION_PATH}.coverageStarts`)}: </Semibold>
            {formatDate(priorAuthorizationCaseData?.prior_authorization?.effective_date, dateFormat)}
          </Body>
          <Body>
            <Semibold>{translate(`${TRANSLATION_PATH}.coverageExpires`)}: </Semibold>
            {formatDate(priorAuthorizationCaseData?.prior_authorization?.expiration_date, dateFormat)}
          </Body>
          {priorAuthorizationCaseData?.is_renewal_available && priorAuthorizationCaseData?.renewal_start_date && (
            <Body>
              <Semibold>{translate(`${TRANSLATION_PATH}.renewalAvailable`)}: </Semibold>
              {formatDate(priorAuthorizationCaseData?.renewal_start_date, dateFormat)}
            </Body>
          )}
        </>
      );
    }

    if (priorAuthorizationCaseData?.case_outcome?.outcome === "denied") {
      return (
        <Stack spacing="medium">
          <Stack spacing="mini">
            <Body>
              <Semibold>{translate(`${TRANSLATION_PATH}.denied`)}: </Semibold>
              {formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}
            </Body>
            {priorAuthorizationCaseData?.case_outcome?.denial_type != null && (
              <Body testId="denialType">
                <Semibold>{translate(`${TRANSLATION_PATH}.denialType`)}: </Semibold>
                {translate(`${TRANSLATION_PATH}.denialTypes.${priorAuthorizationCaseData?.case_outcome?.denial_type}`)}
              </Body>
            )}
            {priorAuthorizationCaseData?.case_outcome?.denial_reason != null && (
              <Body testId="denialReason">
                <Semibold>{translate(`${TRANSLATION_PATH}.denialReason`)}: </Semibold>
                {translate(`${TRANSLATION_PATH}.denialReasons.${priorAuthorizationCaseData?.case_outcome?.denial_reason}`)}
              </Body>
            )}
          </Stack>
          <Body>{translate(`${TRANSLATION_PATH}.moreInformationSeeDocuments`)}</Body>
        </Stack>
      );
    }

    if (priorAuthorizationCaseData?.case_outcome?.outcome === "closed") {
      return (
        <Body>
          <Semibold>{translate(`${TRANSLATION_PATH}.closed`)}: </Semibold>
          {formatDate(priorAuthorizationCaseData?.completed_at, dateFormat, true)}
        </Body>
      );
    }

    return null;
  };

  const handlePrintPage = () => {
    const searchParams = new URLSearchParams();
    searchParams.append("pa_case_id", priorAuthorizationId);
    if (isViewingDataholder) {
      searchParams.append(SearchParamKeys.MEMBER_ELIGIBILITY_MEMBER_ID, memberEligibility.member.id.toString());
    }
    navigateNewTab("/print-prior-authorization-page", searchParams);
  };

  const handlePrintPriorAuthorizationDocument = useCallback(
    async (pa_letter: PriorAuthorizationDecisionSubLetter) => {
      const { url } = await getPriorAuthorizationDocumentsUrl(
        await getAuthToken(),
        {
          id: pa_letter.case_id,
          uuid: pa_letter.uuid,
        },
        memberEligibility,
        isViewingDataholder,
      );

      window.open(url);
    },
    [memberEligibility, isViewingDataholder],
  );

  const { isCompressed } = useResponsive({
    isCompressed: {
      desktop: false,
      tablet: false,
      mobile: true,
    },
  });

  return (
    <>
      {enablePriorAuthorizationRenewalRequest() && (
        <RenewalRequestFormModal
          onClose={() => setRenewalFormIsVisible(null)}
          isVisible={renewalFormIsVisible}
          caseId={priorAuthorizationCaseData?.case_id}
          refresh={refetchPaCase}
        />
      )}
      <PageWrapper>
        <ContentPanel
          title={
            <>
              <Stack spacing="large">
                <Stack>
                  {/* @ts-ignore */}
                  <StackOrShelf alignment={isCompressed ? "left" : "top"}>
                    <Stack spacing="mini" alignment="left">
                      <Shelf spacing="mini">
                        <Icon name="arrow-left" color="turquoise" />
                        {!memberCameFromHomePage && <Link to={"/prior-authorization"}>{translate(`${TRANSLATION_PATH}.priorAuthorization`)}</Link>}
                        {memberCameFromHomePage && <Link to={"/"}>{translate("MENU_BAR.home")}</Link>}
                      </Shelf>
                      <Shelf>
                        <LoadingGroup loading={paCaseIsLoading || paCaseIsRefetching}>
                          <LoadingGroup.Placeholder fallback={<LoadingSkeleton style={{ height: 36, width: 120 }} />}>
                            {!isPAFetchingError && !isLoadingPACaseError && !paCaseIsLoading && !paCaseIsRefetching && (
                              <StackOrShelf>
                                <Subtitle testId="prior-authorization-drug-name">{priorAuthorizationCaseData?.drug_name}</Subtitle>
                                <Shelf alignment="center" spacing="mini">
                                  {priorAuthorizationCaseData?.tags.includes(CaseTags.is_awaiting_prescriber_response)
                                  && caseOutcome == "in_progress" ? (
                                    <Tag type="warning-high-contrast">{translate("PRIOR_AUTHORIZATION.awaitingResponse")}</Tag>
                                  ) : (
                                    <Tag testId="case-outcome" type={caseOutcomesObject[caseOutcome]?.[1] as TagType}>
                                      {caseOutcomesObject[caseOutcome]?.[0]}
                                    </Tag>
                                  )}
                                  {priorAuthorizationCaseData?.is_renewal_available && (
                                    <Tag type="system-alternative-high-contrast">{translate(`${TRANSLATION_PATH}.renewalAvailable`)}</Tag>
                                  )}
                                  {priorAuthorizationCaseData?.next_case_review_level === "renewal" && (
                                    <Tag type="info">{translate(`${TRANSLATION_PATH}.renewalRequested`)}</Tag>
                                  )}
                                </Shelf>
                              </StackOrShelf>
                            )}
                          </LoadingGroup.Placeholder>
                        </LoadingGroup>
                        <Shelf.Spacer />
                      </Shelf>
                    </Stack>
                    <Shelf.Spacer />
                    {!isPAFetchingError && !isLoadingPACaseError && !paCaseIsLoading && !paCaseIsRefetching && (
                      <Button appearance="text" icon="print" onClick={handlePrintPage}>
                        {translate(`${TRANSLATION_PATH}.printPage`)}
                      </Button>
                    )}
                  </StackOrShelf>
                </Stack>
                {!isPAFetchingError
                  && !isLoadingPACaseError
                  && !paCaseIsLoading
                  && !paCaseIsRefetching
                  && caseOutcome == "in_progress"
                  && priorAuthorizationCaseData?.tags.includes(CaseTags.is_awaiting_prescriber_response) && (
                    <Message appearance={Messages.Informational} visible>
                      <Body>{translate(`${TRANSLATION_PATH}.additionalInformationAndDocumentation`)}</Body>
                    </Message>
                  )}
              </Stack>
            </>
          }
          showBorder={false}
        >
          <Stack>
            {enablePriorAuthorizationRenewalRequest() && (
              <Message appearance={Messages.Informational} visible={priorAuthorizationCaseData?.is_renewal_available}>
                <StackOrShelf>
                  <Body>
                    <Trans
                      i18nKey={`${TRANSLATION_PATH}.authorizationRequestExpiresMessage`}
                      values={{
                        drugName: priorAuthorizationCaseData?.drug_name ?? "",
                        date: formatDate(priorAuthorizationCaseData?.prior_authorization?.expiration_date, "MM/DD/YYYY") ?? "",
                      }}
                      components={{ bold: <Bold /> }}
                    />
                  </Body>
                  <Shelf.Spacer />
                  <Shelf>
                    <ThemedButton style={{ padding: "0" }} appearance="text" onClick={() => setRenewalFormIsVisible(true)}>
                      {translate(`${TRANSLATION_PATH}.submitRenewalRequest`)}
                    </ThemedButton>
                    <Shelf.Spacer />
                  </Shelf>
                </StackOrShelf>
              </Message>
            )}
            <LoadingGroup loading={paCaseIsLoading || paCaseIsRefetching}>
              <LoadingGroup.Placeholder fallback={<PADetailsSkeleton />}>
                {!isPAFetchingError && !isLoadingPACaseError && !paCaseIsLoading && !paCaseIsRefetching && (
                  <Stack spacing="large">
                    <Stack spacing="mini">
                      <Body testId="case-id">
                        <Semibold>{translate(`${TRANSLATION_PATH}.id`)}: </Semibold>
                        {priorAuthorizationCaseData?.case_id}
                      </Body>
                      <Body testId="received_at">
                        <Semibold>{translate(`${TRANSLATION_PATH}.requested`)}: </Semibold>
                        {formatDate(priorAuthorizationCaseData?.received_at, dateFormat, true)}
                      </Body>
                      {renderOutcomeSection()}
                    </Stack>
                    <Stack spacing="large">
                      <Stack spacing="small">
                        <SectionHeading>{translate(`${TRANSLATION_PATH}.drugInformation`)}</SectionHeading>
                        <Stack spacing="mini">
                          <Heading>{priorAuthorizationCaseData?.drug_name}</Heading>
                          <Body testId="quantity">
                            <Semibold>{translate(`${TRANSLATION_PATH}.quantity`)}: </Semibold>
                            {priorAuthorizationCaseData?.drug_quantity}
                          </Body>
                          <Body testId="days_supply">
                            <Semibold>{translate(`${TRANSLATION_PATH}.daysSupply`)}: </Semibold>
                            {priorAuthorizationCaseData?.drug_days_supply}
                          </Body>
                          <Body testId="directions_for_use">
                            <Semibold>{translate(`${TRANSLATION_PATH}.directionsForUse`)}: </Semibold>
                            {priorAuthorizationCaseData?.drug_directions_for_use}
                          </Body>
                        </Stack>
                      </Stack>
                      <Stack spacing="small">
                        <SectionHeading>{translate(`${TRANSLATION_PATH}.prescriber`)}</SectionHeading>
                        <Stack spacing="mini">
                          <Heading>
                            {priorAuthorizationCaseData?.prescriber_first_name + " " + priorAuthorizationCaseData?.prescriber_last_name}
                          </Heading>
                          <Body testId="prescriber-npi">
                            <Semibold>{translate(`${TRANSLATION_PATH}.npi`)}: </Semibold>
                            {priorAuthorizationCaseData?.prescriber_npi}
                          </Body>
                          <Body testId="prescriber-phone">
                            <Semibold>{translate(`${TRANSLATION_PATH}.phone`)}: </Semibold>
                            <a href={`tel:${formattedPrescriberPhoneNumber}`}>{formattedPrescriberPhoneNumber}</a>
                          </Body>
                        </Stack>
                      </Stack>
                      <Stack spacing="small">
                        <SectionHeading>{translate(`${TRANSLATION_PATH}.documents`)}</SectionHeading>
                        {!isLoadingDocument && !isErrorDocument && (
                          <Stack spacing="small" alignment="left">
                            {+documentListData?.result_count == 0 && <Body> {translate(`${TRANSLATION_PATH}.none`)} </Body>}
                            {documentListData?.results
                              && +documentListData?.result_count >= 0
                              && documentListData.results.map((pa_document) => (
                                <Button
                                  key={pa_document.letter.uuid}
                                  appearance={Buttons.Functional.Tertiary.Compact}
                                  icon="link-out"
                                  onClick={() => handlePrintPriorAuthorizationDocument(pa_document.letter)}
                                >
                                  {pa_document.letter.filename}
                                </Button>
                              ))}
                          </Stack>
                        )}
                      </Stack>
                      <Stack spacing="small">
                        <SectionHeading>{translate(`${TRANSLATION_PATH}.customerSupport`)}</SectionHeading>
                        <Body>
                          {translate(`${TRANSLATION_PATH}.contactCustomerCareAt`)}{" "}
                          <a href={`tel:${customerSupportNumber}`}>{customerSupportNumber}</a>.
                        </Body>
                      </Stack>
                    </Stack>
                  </Stack>
                )}
                {(isPAFetchingError || isLoadingPACaseError) && <NoResults message={translate(`${TRANSLATION_PATH}.anErrorOccurred`)} />}
              </LoadingGroup.Placeholder>
            </LoadingGroup>
          </Stack>
        </ContentPanel>
      </PageWrapper>
    </>
  );
};

const PADetailsSectionSkeleton = () => {
  return (
    <Stack spacing="mini">
      <LoadingSkeleton style={{ height: 15, width: 125 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
    </Stack>
  );
};

const PADetailsSkeleton = () => {
  return (
    <Stack spacing="large">
      <PADetailsSectionSkeleton />
      <PADetailsSectionSkeleton />
      <PADetailsSectionSkeleton />
      <LoadingSkeleton style={{ height: 15, width: 125 }} />
      <LoadingSkeleton style={{ height: 20, width: 250 }} />
    </Stack>
  );
};

const SectionHeading = styled.p`
  font-size: 0.75rem;
  line-height: 0.75rem;
  text-transform: uppercase;
  color: #60646d;
  font-weight: 600;
`;
